import { useEffect, useState, Fragment } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, ApprovalTimeline } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Airplane, FileText, Check, X, Clock, ArrowUUpLeft, UserSwitch, CaretDown } from "@phosphor-icons/react";

export default function PendingApprovals() {
  const [data, setData] = useState({ leave: [], timesheets: [], counts: {}, stage: "first" });
  const [tab, setTab] = useState("leave");
  const [modal, setModal] = useState(null); // {kind, id, action:'reject'|'sendback'}
  const [reason, setReason] = useState("");
  const [sel, setSel] = useState([]);
  const [deleg, setDeleg] = useState({ mine: null, candidates: [], covering: [] });
  const [delegForm, setDelegForm] = useState({ delegate_id: "", enabled: false, until: "" });
  const [openRow, setOpenRow] = useState(null);
  const isFinal = data.stage === "final";

  useEffect(() => { setSel([]); }, [tab]);

  const rows = tab === "leave" ? data.leave : data.timesheets;
  const allSelected = rows.length > 0 && sel.length === rows.length;
  const toggleAll = () => setSel(allSelected ? [] : rows.map((r) => r.id));
  const toggleOne = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const load = async () => {
    try { const r = await api.get("/approvals/pending"); setData(r.data); window.dispatchEvent(new Event("approvals-changed")); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadDeleg = async () => {
    try {
      const r = await api.get("/delegation");
      setDeleg(r.data);
      if (r.data.mine) setDelegForm({ delegate_id: r.data.mine.delegate_id || "", enabled: !!r.data.mine.enabled, until: r.data.mine.until || "" });
    } catch (_) {}
  };
  useEffect(() => { load(); loadDeleg(); }, []);

  const saveDeleg = async (enabled) => {
    try {
      await api.put("/delegation", { ...delegForm, enabled });
      toast.success(enabled ? "Delegation enabled" : "Delegation turned off");
      setDelegForm((f) => ({ ...f, enabled })); loadDeleg();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const bulkApprove = async () => {
    if (sel.length === 0) return;
    try {
      if (tab === "leave") await api.post("/leave/bulk-review", { leave_ids: sel, status: "approved" });
      else await api.post("/timesheets/weeks/bulk-review", { week_ids: sel, status: "approved" });
      toast.success(isFinal ? `Final-approved ${sel.length}` : `Approved ${sel.length}`); setSel([]); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approveLeave = async (id) => {
    try { const r = await api.post(`/leave/${id}/review`, { status: "approved" }); toast.success(r.data?.status === "approved" ? "Leave approved" : "Approved — sent to Super Admin"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const approveWeek = async (id) => {
    try { const r = await api.post(`/timesheets/weeks/${id}/review`, { status: "approved" }); toast.success(r.data?.status === "approved" ? "Timesheet approved" : "Approved — sent to Super Admin"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const snooze = async (type, id) => {
    try { await api.post("/approvals/snooze", { type, id, days: 1 }); toast.success("Snoozed for 1 day"); setSel((s) => s.filter((x) => x !== id)); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submitModal = async () => {
    const action = modal.action;
    if (action === "reject" && !reason.trim()) { toast.error("Rejection reason is required"); return; }
    const status = action === "reject" ? "rejected" : "sent_back";
    try {
      if (modal.kind === "bulk") {
        if (tab === "leave") await api.post("/leave/bulk-review", { leave_ids: sel, status, reason });
        else await api.post("/timesheets/weeks/bulk-review", { week_ids: sel, status, reason });
        toast.success(`${action === "reject" ? "Rejected" : "Sent back"} ${sel.length}`); setSel([]);
      } else if (modal.kind === "leave") {
        await api.post(`/leave/${modal.id}/review`, { status, reason });
        toast.success(action === "reject" ? "Rejected" : "Sent back to admin");
      } else {
        await api.post(`/timesheets/weeks/${modal.id}/review`, { status, reason });
        toast.success(action === "reject" ? "Rejected" : "Sent back to admin");
      }
      setModal(null); setReason(""); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const c = data.counts || {};

  return (
    <div data-testid="pending-approvals-page">
      <PageHeader overline={isFinal ? "Final Approval · Super Admin" : "First-level Approval"} title="Pending Approvals"
        subtitle={isFinal
          ? "Final sign-off on requests already approved by Team Admins"
          : "Review your team's requests, then they go to the Super Admin for final approval"} />

      {deleg.covering && deleg.covering.length > 0 && (
        <div className="mb-4 p-3 border border-[#1F4E8C] bg-[#1F4E8C]/5 text-sm flex items-center gap-2" data-testid="covering-banner">
          <UserSwitch size={18} className="text-[#1F4E8C]" />
          You're covering approvals for <strong>{deleg.covering.map((c) => c.delegator_name).join(", ")}</strong> — their team's requests appear in your queue below.
        </div>
      )}

      {!isFinal && (
        <div className="mb-4 bg-white border border-[#E5E5E5]" data-testid="delegation-card">
          <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#F7F7F5] flex items-center gap-2">
            <UserSwitch size={18} className="text-[#1F4E8C]" />
            <div className="font-head font-semibold text-sm">Delegate my approvals</div>
            {delegForm.enabled && <span className="ml-2 text-[11px] uppercase tracking-wider px-2 py-0.5 bg-emerald-100 text-emerald-700">Active</span>}
          </div>
          <div className="px-4 py-3 flex flex-wrap items-end gap-3">
            <div>
              <div className="eyebrow mb-1">Cover for me</div>
              <select value={delegForm.delegate_id} onChange={(e) => setDelegForm({ ...delegForm, delegate_id: e.target.value })}
                className="h-10 border border-[#E5E5E5] px-2 bg-white text-sm min-w-[200px]" data-testid="delegate-select">
                <option value="">Select a Team Admin…</option>
                {deleg.candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="eyebrow mb-1">Until (optional)</div>
              <Input type="date" value={delegForm.until || ""} onChange={(e) => setDelegForm({ ...delegForm, until: e.target.value })}
                className="rounded-none w-40 h-10" data-testid="delegate-until" />
            </div>
            {delegForm.enabled ? (
              <Button onClick={() => saveDeleg(false)} className="rounded-none bg-neutral-200 text-neutral-800 hover:bg-neutral-300" data-testid="delegate-disable">Turn off</Button>
            ) : (
              <Button onClick={() => saveDeleg(true)} className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white" data-testid="delegate-enable">Enable cover</Button>
            )}
            <div className="text-xs text-neutral-500 max-w-[320px]">While on leave, the chosen Team Admin can approve your team's requests on your behalf.</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("leave")} data-testid="tab-leave"
          className={`px-4 h-10 text-sm font-semibold flex items-center gap-2 border ${tab === "leave" ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white text-[#1F4E8C] border-[#E5E5E5]"}`}>
          <Airplane size={16} /> Leave <span className="bg-[#FF5500] text-white text-xs px-1.5 rounded-full">{c.leave || 0}</span>
        </button>
        <button onClick={() => setTab("timesheets")} data-testid="tab-timesheets"
          className={`px-4 h-10 text-sm font-semibold flex items-center gap-2 border ${tab === "timesheets" ? "bg-[#1F4E8C] text-white border-[#1F4E8C]" : "bg-white text-[#1F4E8C] border-[#E5E5E5]"}`}>
          <FileText size={16} /> Timesheets <span className="bg-[#FF5500] text-white text-xs px-1.5 rounded-full">{c.timesheets || 0}</span>
        </button>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-[#1F4E8C] text-white px-4 py-2.5" data-testid="bulk-bar">
          <span className="text-sm font-semibold">{sel.length} selected</span>
          <div className="ml-auto flex gap-2">
            <button onClick={bulkApprove} data-testid="bulk-approve" className="text-xs font-semibold px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700">{isFinal ? "Final-approve selected" : "Approve selected"}</button>
            <button onClick={() => setModal({ kind: "bulk", action: "reject" })} data-testid="bulk-reject" className="text-xs font-semibold px-3 py-1.5 bg-red-600 hover:bg-red-700">Reject selected</button>
            <button onClick={() => setSel([])} className="text-xs px-3 py-1.5 bg-white/20">Clear</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5]">
        {tab === "leave" ? (
          data.leave.length === 0 ? <Empty label="No pending leave requests" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-[#E5E5E5] bg-[#F7F7F5]">
                <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" /></th>
                <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Dates</th><th className="px-4 py-3">Days</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Reason</th><th className="px-4 py-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {data.leave.map((l) => (
                  <Fragment key={l.id}>
                  <tr className="border-b border-[#F0F0EE]" data-testid={`leave-row-${l.id}`}>
                    <td className="px-4 py-2.5"><input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggleOne(l.id)} data-testid={`select-${l.id}`} /></td>
                    <td className="px-4 py-2.5 font-medium">{l.user_name}</td>
                    <td className="px-4 py-2.5">{l.leave_type}</td>
                    <td className="px-4 py-2.5 text-xs">{l.from_date} → {l.to_date}</td>
                    <td className="px-4 py-2.5">{l.days}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-2.5 text-xs text-neutral-500 max-w-[200px] truncate">{l.reason}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <ActionButtons isFinal={isFinal} onApprove={() => approveLeave(l.id)}
                        onReject={() => setModal({ kind: "leave", id: l.id, action: "reject" })}
                        onSendBack={() => setModal({ kind: "leave", id: l.id, action: "sendback" })}
                        onSnooze={() => snooze("leave", l.id)}
                        onHistory={() => setOpenRow(openRow === l.id ? null : l.id)} testid={l.id} />
                    </td>
                  </tr>
                  {openRow === l.id && (
                    <tr className="bg-[#F7F7F5] border-b border-[#F0F0EE]"><td colSpan={8} className="px-6 py-4">
                      <div className="eyebrow mb-3 text-neutral-500">Approval timeline</div>
                      <ApprovalTimeline events={l.events} />
                    </td></tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )
        ) : (
          data.timesheets.length === 0 ? <Empty label="No pending timesheets" /> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-[#E5E5E5] bg-[#F7F7F5]">
                <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" /></th>
                <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Week</th>
                <th className="px-4 py-3">Hours</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr></thead>
              <tbody>
                {data.timesheets.map((w) => (
                  <Fragment key={w.id}>
                  <tr className="border-b border-[#F0F0EE]" data-testid={`week-row-${w.id}`}>
                    <td className="px-4 py-2.5"><input type="checkbox" checked={sel.includes(w.id)} onChange={() => toggleOne(w.id)} data-testid={`select-${w.id}`} /></td>
                    <td className="px-4 py-2.5 font-medium">{w.user_name}</td>
                    <td className="px-4 py-2.5 text-xs">{w.week_start} → {w.week_end}</td>
                    <td className="px-4 py-2.5 font-head font-semibold">{w.total_hours}h</td>
                    <td className="px-4 py-2.5"><StatusBadge status={w.status} direct={!w.reviewed_by_id} /></td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <ActionButtons isFinal={isFinal} onApprove={() => approveWeek(w.id)}
                        onReject={() => setModal({ kind: "timesheet", id: w.id, action: "reject" })}
                        onSendBack={w.reviewed_by_id ? () => setModal({ kind: "timesheet", id: w.id, action: "sendback" }) : null}
                        onSnooze={() => snooze("timesheet", w.id)}
                        onHistory={() => setOpenRow(openRow === w.id ? null : w.id)} testid={w.id} />
                    </td>
                  </tr>
                  {openRow === w.id && (
                    <tr className="bg-[#F7F7F5] border-b border-[#F0F0EE]"><td colSpan={6} className="px-6 py-4">
                      <div className="eyebrow mb-3 text-neutral-500">Approval timeline</div>
                      <ApprovalTimeline events={w.events} />
                    </td></tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      <Dialog open={!!modal} onOpenChange={(v) => { if (!v) { setModal(null); setReason(""); } }}>
        <DialogContent className="rounded-none" data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-head">
            {modal?.action === "sendback" ? "Send back to admin" : "Reject — reason required"}
          </DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className="rounded-none"
            placeholder={modal?.action === "sendback" ? "Add a note for the admin (optional)…" : "Explain why this is being rejected…"} data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setModal(null); setReason(""); }} className="rounded-none">Cancel</Button>
            {modal?.action === "sendback" ? (
              <Button onClick={submitModal} className="rounded-none bg-[#1F4E8C] hover:bg-[#163a68] text-white" data-testid="confirm-sendback">Send back</Button>
            ) : (
              <Button onClick={submitModal} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject">Reject</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActionButtons({ isFinal, onApprove, onReject, onSendBack, onSnooze, onHistory, testid }) {
  return (
    <div className="inline-flex gap-2">
      <button onClick={onApprove} data-testid={`approve-${testid}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700"><Check size={14} />{isFinal ? "Final Approve" : "Approve"}</button>
      {isFinal && onSendBack && (
        <button onClick={onSendBack} data-testid={`sendback-${testid}`} title="Return to the Team Admin for re-review" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-[#1F4E8C] text-[#1F4E8C] hover:bg-[#1F4E8C]/5"><ArrowUUpLeft size={14} />Send Back</button>
      )}
      <button onClick={onReject} data-testid={`reject-${testid}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-red-300 text-red-600 hover:bg-red-50"><X size={14} />Reject</button>
      <button onClick={onSnooze} data-testid={`snooze-${testid}`} title="Hide from your pending list for 1 day" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-neutral-600 hover:bg-neutral-50"><Clock size={14} />Snooze 1d</button>
      <button onClick={onHistory} data-testid={`history-${testid}`} title="Approval timeline" className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-neutral-600 hover:bg-neutral-50"><CaretDown size={14} />History</button>
    </div>
  );
}

function Empty({ label }) {
  return <div className="px-4 py-12 text-center text-neutral-500 text-sm">{label}</div>;
}
